import { collection, doc, getDoc, setDoc, deleteDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { MindshareHistoryEntry, SubnetMindshare } from '../types/mindshare';
import { subnetNames } from '../utils/subnetNames';
import { clearAllCaches, getCorrectSubnetName } from '../utils/cacheUtils';

/**
 * Stores the most recent mindshare history entry for a subnet in the current_mindshare collection
 * Replaces any existing entry for that subnet
 */
export async function updateCurrentMindshare(netuid: number, historyEntry: MindshareHistoryEntry): Promise<boolean> {
  try {
    // Create a document reference for this subnet in the current_mindshare collection
    const currentMindshareRef = doc(db, 'current_mindshare', netuid.toString());
    
    // Check if there's an existing entry
    const existingDoc = await getDoc(currentMindshareRef);
    
    // If there's an existing entry with an older timestamp, delete it
    if (existingDoc.exists()) {
      const existingData = existingDoc.data() as MindshareHistoryEntry;
      const existingTimestamp = new Date(existingData.timestamp);
      const newTimestamp = new Date(historyEntry.timestamp);
      
      // Only update if the new entry is more recent
      if (newTimestamp <= existingTimestamp) {
        console.log(`Skipping update for subnet ${netuid}: new entry is not more recent`);
        return false;
      }
    }
    
    // Get the correct subnet name using our utility function
    const correctName = getCorrectSubnetName(netuid, subnetNames);
    
    // Make sure percentChange is included in the entry
    const entryToSave = {
      ...historyEntry,
      netuid,
      name: correctName, // Ensure we use the correct name
      percentChange: historyEntry.percentChange !== undefined ? historyEntry.percentChange : 0
    };
    
    // Store the new entry
    await setDoc(currentMindshareRef, entryToSave);
    
    console.log(`Updated current mindshare for subnet ${netuid} with percentChange: ${entryToSave.percentChange}`);
    return true;
  } catch (error) {
    console.error(`Error updating current mindshare for subnet ${netuid}:`, error);
    return false;
  }
}

/**
 * Gets the current mindshare entry for a specific subnet
 */
export async function getCurrentMindshare(netuid: number): Promise<MindshareHistoryEntry | null> {
  try {
    const docRef = doc(db, 'current_mindshare', netuid.toString());
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as MindshareHistoryEntry;
    }
    
    return null;
  } catch (error) {
    console.error(`Error getting current mindshare for subnet ${netuid}:`, error);
    return null;
  }
}

/**
 * Gets all current mindshare entries
 */
export async function getAllCurrentMindshare(): Promise<(MindshareHistoryEntry & { netuid: number, name: string })[]> {
  try {
    const collectionRef = collection(db, 'current_mindshare');
    const snapshot = await getDocs(collectionRef);
    
    return snapshot.docs.map(doc => {
      const data = doc.data() as MindshareHistoryEntry & { netuid: number, name: string };
      const netuid = parseInt(doc.id);
      
      // Get the correct subnet name using our utility function
      const correctName = getCorrectSubnetName(netuid, subnetNames);
      
      return {
        ...data,
        netuid,
        name: correctName // Ensure we use the up-to-date name
      };
    });
  } catch (error) {
    console.error('Error getting all current mindshare entries:', error);
    return [];
  }
}

/**
 * Gets the top gainers based on percentChange in current mindshare
 */
export async function getTopGainers(limit: number = 5): Promise<(MindshareHistoryEntry & { netuid: number, name: string })[]> {
  try {
    const collectionRef = collection(db, 'current_mindshare');
    const snapshot = await getDocs(collectionRef);
    
    const entries = snapshot.docs
      .map(doc => {
        const data = doc.data() as MindshareHistoryEntry & { netuid: number, name: string };
        const netuid = parseInt(doc.id);
        
        // Get the correct subnet name using our utility function
        const correctName = getCorrectSubnetName(netuid, subnetNames);
        
        return {
          ...data,
          netuid,
          name: correctName // Ensure we use the up-to-date name
        };
      })
      .filter(entry => entry.percentChange !== undefined && entry.percentChange > 0)
      .sort((a, b) => (b.percentChange || 0) - (a.percentChange || 0))
      .slice(0, limit);
    
    console.log(`Found ${entries.length} gainers from current_mindshare`);
    return entries;
  } catch (error) {
    console.error('Error getting top gainers:', error);
    return [];
  }
}

/**
 * Gets the top losers based on percentChange in current mindshare
 */
export async function getTopLosers(limit: number = 5): Promise<(MindshareHistoryEntry & { netuid: number, name: string })[]> {
  try {
    const collectionRef = collection(db, 'current_mindshare');
    const snapshot = await getDocs(collectionRef);
    
    const entries = snapshot.docs
      .map(doc => {
        const data = doc.data() as MindshareHistoryEntry & { netuid: number, name: string };
        const netuid = parseInt(doc.id);
        
        // Get the correct subnet name using our utility function
        const correctName = getCorrectSubnetName(netuid, subnetNames);
        
        return {
          ...data,
          netuid,
          name: correctName // Ensure we use the up-to-date name
        };
      })
      .filter(entry => entry.percentChange !== undefined && entry.percentChange < 0)
      .sort((a, b) => (a.percentChange || 0) - (b.percentChange || 0))
      .slice(0, limit);
    
    console.log(`Found ${entries.length} losers from current_mindshare`);
    return entries;
  } catch (error) {
    console.error('Error getting top losers:', error);
    return [];
  }
}

/**
 * Updates the current mindshare collection with the latest history entries for all subnets
 */
export async function updateAllCurrentMindshare(): Promise<number> {
  try {
    // Clear all caches before updating
    clearAllCaches();
    
    // Get all subnet history collections
    const historyCollectionRef = collection(db, 'subnet_history');
    const historySnapshot = await getDocs(historyCollectionRef);
    
    let updatedCount = 0;
    
    // Process each subnet
    for (const historyDoc of historySnapshot.docs) {
      const netuid = parseInt(historyDoc.id);
      
      try {
        // Get the most recent history entry for this subnet
        const entriesRef = collection(db, 'subnet_history', historyDoc.id, 'entries');
        const entriesQuery = query(entriesRef, orderBy('timestamp', 'desc'), limit(2)); // Get 2 most recent to calculate percent change
        const entriesSnapshot = await getDocs(entriesQuery);
        
        if (entriesSnapshot.size >= 2) {
          // Get the two most recent entries to calculate percent change
          const entries = entriesSnapshot.docs.map(doc => doc.data() as MindshareHistoryEntry);
          const latestEntry = entries[0];
          const previousEntry = entries[1];
          
          // Calculate percent change if not already present
          if (latestEntry.percentChange === undefined && previousEntry.mindsharePercentage > 0) {
            const percentChange = ((latestEntry.mindsharePercentage - previousEntry.mindsharePercentage) / 
                                  previousEntry.mindsharePercentage) * 100;
            latestEntry.percentChange = percentChange;
          }
          
          // Update the current mindshare collection
          const updated = await updateCurrentMindshare(netuid, latestEntry);
          if (updated) {
            updatedCount++;
          }
        } else if (entriesSnapshot.size === 1) {
          // If there's only one entry, use it but set percentChange to 0
          const latestEntry = entriesSnapshot.docs[0].data() as MindshareHistoryEntry;
          if (latestEntry.percentChange === undefined) {
            latestEntry.percentChange = 0;
          }
          
          const updated = await updateCurrentMindshare(netuid, latestEntry);
          if (updated) {
            updatedCount++;
          }
        }
      } catch (error) {
        console.error(`Error processing subnet ${netuid}:`, error);
        continue;
      }
    }
    
    console.log(`Updated ${updatedCount} subnets in current_mindshare collection`);
    return updatedCount;
  } catch (error) {
    console.error('Error updating all current mindshare entries:', error);
    return 0;
  }
}